import React, { useState } from 'react';
import { FiX, FiSend } from 'react-icons/fi';
import api from '../../utils/api';

const SendMessageModal = ({ ticketId, onClose, onSuccess }) => {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  // Quick response templates
  const quickResponses = [
    "We're looking into your issue and will get back to you shortly.",
    "Thank you for contacting us. We understand your concern and are working on it.",
    "We apologize for the inconvenience caused. Our team is working to resolve this.",
    "We've contacted the salon and will update you once we hear back from them.",
    "Your refund has been initiated and will be processed within 5-7 business days."
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      alert('Please enter a message');
      return;
    }

    setSending(true);
    try {
      const response = await api.post(`/support/tickets/${ticketId}/message`, {
        message: message.trim()
      });

      if (response.data.success) {
        onSuccess();
        onClose();
      }
    } catch (error) {
      console.error('Error sending message:', error);
      alert(error.response?.data?.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const insertQuickResponse = (text) => {
    setMessage(prev => prev ? `${prev}\n\n${text}` : text);
  };

  return (
    <div className="fixed inset-0 bg-half-transparent flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">Send Message to User</h3>
          <button
            onClick={onClose}
            className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-4">
          {/* Quick Responses */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quick Responses
            </label>
            <div className="flex flex-wrap gap-2">
              {quickResponses.map((text, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => insertQuickResponse(text)}
                  className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors truncate max-w-xs"
                  title={text}
                >
                  {text.substring(0, 40)}...
                </button>
              ))}
            </div>
          </div>

          {/* Message Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message <span className="text-red-500">*</span>
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your message here..."
              rows={5}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              This message will be sent to the user and they will receive a push notification.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={sending || !message.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {sending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Sending...
                </>
              ) : (
                <>
                  <FiSend size={16} />
                  Send Message
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SendMessageModal;
